import { usePathwayCommand } from '../pathways/usePathwayCommand';
import { defineWorksheetTemplateInputSchema } from '@cc/schema';
import { useTranslate } from '@refinedev/core';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Input,
  List,
  Space,
  Tag,
  Typography,
} from 'antd';
import { useState } from 'react';
import { ImportNotice } from './ImportNotice';
import {
  draftFromImport,
  intakePacketSections,
  pagesLabel,
  questionCount,
  type ImportItem,
  type ImportResult,
} from './importModel';
import { contentOfDraft } from './model';
import { useWorksheetCommand } from './useWorksheetCommand';
import { WorksheetBuilder } from './WorksheetBuilder';

/**
 * E34 · Practice — Worksheets — Builder — Imported draft, a PACKET (block 5b; screens e34e0009-…-0034 and
 * "Imported packet — Make an intake packet (section)" e34e0009-…-0044). The import's result as a document list,
 * NOT saved (Q-e34-15 A): the drafts to check, the known standard questionnaires linked to CopingCard's
 * assessment catalogue (never rebuilt as worksheets — Q-e34-9; one not in the catalogue is listed "not added" —
 * E35 decides what may be sent), and the parts left out with their reason. Behavior (the board's):
 *   · Check and edit → the draft in the Builder; its Save is DefineWorksheetTemplate with `importedFrom`;
 *   · Save all → one DefineWorksheetTemplate per checked draft — one command per template from one screen (the
 *     Q-e34-5 shape), each outcome shown against its row;
 *   · Make an intake packet → Save all, then the section: the name (prefilled), a read-only step list — every
 *     saved template and every catalogue questionnaire, released immediately — and Make the packet →
 *     DefinePathway (visibility practice);
 *   · Discard → back to the library; nothing was stored.
 */
type Saved = { templateId: string; name: string };

export const ImportedPacket = ({
  result,
  rightsConfirmed = false,
  onDone,
}: {
  result: ImportResult;
  /** Q-ugc-2 A — the tick given at the import: carried on every Save here; the packet never asks. */
  rightsConfirmed?: boolean;
  onDone: (to: 'library' | 'pathways') => void;
}) => {
  const t = useTranslate();
  const { message } = AntdApp.useApp();
  const { run } = useWorksheetCommand();
  const pathway = usePathwayCommand();
  const items = result.drafts;
  const templates = items
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item.kind === 'template' && item.draft);
  const instruments = items.filter((i) => i.kind === 'knownInstrument');
  const skipped = items.filter((i) => i.kind === 'skip');

  const [checked, setChecked] = useState<Record<number, boolean>>(
    Object.fromEntries(templates.map(({ index }) => [index, true])),
  );
  const [saved, setSaved] = useState<Record<number, Saved>>({});
  const [rowError, setRowError] = useState<Record<number, string>>({});
  const [editing, setEditing] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const [packetName, setPacketName] = useState<string>(t('worksheets.import.packet.make.nameDefault'));
  const [packetDescription, setPacketDescription] = useState('');
  const [packetBusy, setPacketBusy] = useState(false);
  const [packetProblem, setPacketProblem] = useState<string | null>(null);

  const importedFrom = result.importedFrom;

  /** Save every checked draft not saved yet — one command each; the outcome against its row. */
  const saveAll = async (): Promise<Record<number, Saved>> => {
    setProblem(null);
    const next: Record<number, Saved> = { ...saved };
    const errors: Record<number, string> = {};
    const todo = templates.filter(({ index }) => checked[index] && !next[index]);
    if (!todo.length && !Object.keys(next).length) {
      setProblem(t('worksheets.import.packet.nothingToSave'));
      return next;
    }
    setSaving(true);
    let count = 0;
    for (const { item, index } of todo) {
      const draft = draftFromImport(item.draft as NonNullable<ImportItem['draft']>);
      const input = {
        ...contentOfDraft(draft),
        copiedFrom: null,
        ...(rightsConfirmed ? { rightsConfirmed: true } : {}),
        ...(importedFrom ? { importedFrom } : {}),
      };
      try {
        await defineWorksheetTemplateInputSchema.validate(input);
      } catch {
        errors[index] = t('worksheets.builder.errors.incomplete');
        continue;
      }
      const r = await run('defineWorksheetTemplate', input, t('worksheets.builder.failed'));
      if (r.ok) {
        next[index] = { templateId: r.templateId, name: draft.name.trim() };
        count += 1;
      } else {
        errors[index] = r.message;
      }
    }
    setSaving(false);
    setSaved(next);
    setRowError(errors);
    if (count) message.success(t('worksheets.import.packet.savedAll', { count }));
    return next;
  };

  const startPacket = async () => {
    const all = await saveAll();
    if (!Object.keys(all).length) {
      setPacketProblem(t('worksheets.import.packet.make.nothing'));
      return;
    }
    setPacketProblem(null);
    setMaking(true);
  };

  const catalogue = instruments
    .filter((i) => i.inCatalogue && i.instrumentId)
    .map((i) => ({ instrumentId: i.instrumentId as string, name: i.instrument ?? (i.instrumentId as string) }));
  const savedList = templates
    .filter(({ index }) => saved[index])
    .map(({ index }) => saved[index]);
  const sections = intakePacketSections(
    savedList,
    catalogue,
    t('worksheets.import.packet.make.section'),
  );

  const makePacket = async () => {
    if (!packetName.trim()) return;
    setPacketBusy(true);
    setPacketProblem(null);
    const r = await pathway.run(
      'definePathway',
      {
        name: packetName.trim(),
        description: packetDescription.trim() || null,
        visibility: 'practice',
        sections,
        copiedFrom: null,
      },
      t('worksheets.import.packet.make.failed'),
    );
    setPacketBusy(false);
    if (!r.ok) {
      setPacketProblem(r.message);
      return;
    }
    message.success(t('worksheets.import.packet.make.made'));
    onDone('pathways');
  };

  if (editing !== null) {
    const item = items[editing];
    return (
      <WorksheetBuilder
        key={`import-${result.importId}-${editing}`}
        mode="define"
        initial={draftFromImport(item.draft as NonNullable<ImportItem['draft']>)}
        importedFrom={importedFrom}
        rightsConfirmed={rightsConfirmed}
        notice={<ImportNotice importedFrom={importedFrom} notes={item.notes ?? []} />}
        onSaved={(s) => {
          setSaved((prev) => ({
            ...prev,
            [editing]: { templateId: s.templateId, name: item.draft?.name ?? '' },
          }));
          setEditing(null);
        }}
        onCancel={() => setEditing(null)}
        cancelLabel={t('worksheets.import.draft.back')}
        heading={
          <Typography.Title level={3} style={{ margin: 0 }}>
            {item.draft?.name || t('worksheets.builder.untitled')}
          </Typography.Title>
        }
      />
    );
  }

  const summary = [
    t('worksheets.import.packet.summaryDrafts', { count: templates.length }),
    t('worksheets.import.packet.summaryInstruments', { count: catalogue.length }),
    t('worksheets.import.packet.summarySkipped', { count: skipped.length }),
  ].join(' · ');

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }} data-testid="worksheet-import-packet">
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t('worksheets.import.packet.title')}
        </Typography.Title>
        <Space wrap>
          <Button onClick={() => onDone('library')} data-testid="worksheet-import-discard">
            {t('worksheets.import.packet.discard')}
          </Button>
          <Button loading={saving} onClick={saveAll} data-testid="worksheet-import-save-all">
            {t('worksheets.import.packet.saveAll')}
          </Button>
          <Button type="primary" loading={saving} onClick={startPacket} data-testid="worksheet-import-make-packet">
            {t('worksheets.import.packet.makePacket')}
          </Button>
        </Space>
      </Space>
      <ImportNotice importedFrom={importedFrom} notes={[]} />
      <Typography.Text strong data-testid="worksheet-import-summary">
        {summary}
      </Typography.Text>
      {problem ? <Alert type="error" showIcon message={problem} data-testid="worksheet-import-packet-error" /> : null}

      <Card title={t('worksheets.import.packet.drafts')} size="small">
        <List
          dataSource={templates}
          renderItem={({ item, index }) => (
            <List.Item
              data-testid={`worksheet-import-draft-${index}`}
              actions={[
                saved[index] ? (
                  <Tag color="green" key="saved" data-testid={`worksheet-import-saved-${index}`}>
                    {t('worksheets.import.packet.saved')}
                  </Tag>
                ) : (
                  <Button
                    key="check"
                    size="small"
                    onClick={() => setEditing(index)}
                    data-testid={`worksheet-import-check-${index}`}
                  >
                    {t('worksheets.import.packet.check')}
                  </Button>
                ),
              ]}
            >
              <Space direction="vertical" size={0} style={{ width: '100%' }}>
                <Space wrap>
                  <Checkbox
                    checked={!!checked[index] || !!saved[index]}
                    disabled={!!saved[index]}
                    onChange={(e) => setChecked({ ...checked, [index]: e.target.checked })}
                    data-testid={`worksheet-import-tick-${index}`}
                  />
                  <Typography.Text strong>{item.draft?.name || t('worksheets.builder.untitled')}</Typography.Text>
                  <Tag>{t(`worksheets.category.${item.draft?.category}`)}</Tag>
                  {item.draft?.signatureRequired ? <Tag color="blue">{t('worksheets.import.packet.signature')}</Tag> : null}
                </Space>
                <Typography.Text type="secondary">
                  {t('worksheets.import.notes.pages', { pages: pagesLabel(item.pages), count: item.pages.length })}
                  {' · '}
                  {t('worksheets.import.packet.questions', { count: questionCount(item.draft) })}
                </Typography.Text>
                {rowError[index] ? (
                  <Typography.Text type="danger" data-testid={`worksheet-import-row-error-${index}`}>
                    {rowError[index]}
                  </Typography.Text>
                ) : null}
              </Space>
            </List.Item>
          )}
        />
      </Card>

      {instruments.length ? (
        <Card title={t('worksheets.import.packet.instruments')} size="small">
          <List
            dataSource={instruments}
            renderItem={(i, n) => (
              <List.Item data-testid={`worksheet-import-instrument-${n}`}>
                <Space wrap>
                  <Typography.Text strong>{i.instrument}</Typography.Text>
                  <Typography.Text type="secondary">
                    {t('worksheets.import.notes.pages', { pages: pagesLabel(i.pages), count: i.pages.length })}
                  </Typography.Text>
                  {i.inCatalogue ? (
                    <Tag color="green" data-testid={`worksheet-import-instrument-${n}-in`}>
                      {t('worksheets.import.packet.inCatalogue')}
                    </Tag>
                  ) : (
                    <Tag data-testid={`worksheet-import-instrument-${n}-out`}>
                      {t('worksheets.import.packet.notInCatalogue')}
                    </Tag>
                  )}
                </Space>
              </List.Item>
            )}
          />
        </Card>
      ) : null}

      {skipped.length ? (
        <Card title={t('worksheets.import.packet.skippedTitle')} size="small">
          <List
            dataSource={skipped}
            renderItem={(s, n) => (
              <List.Item data-testid={`worksheet-import-skipped-${n}`}>
                <Typography.Text type="secondary">
                  {t('worksheets.import.notes.pages', { pages: pagesLabel(s.pages), count: s.pages.length })}
                  {' · '}
                  {s.reason
                    ? t('worksheets.import.packet.skipped', { reason: s.reason })
                    : t('worksheets.import.packet.skippedNoReason')}
                </Typography.Text>
              </List.Item>
            )}
          />
        </Card>
      ) : null}

      {packetProblem && !making ? (
        <Alert type="error" showIcon message={packetProblem} data-testid="worksheet-import-packet-make-error" />
      ) : null}
      {making ? (
        <Card title={t('worksheets.import.packet.make.title')} data-testid="worksheet-import-make">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Typography.Text>{t('worksheets.import.packet.make.intro')}</Typography.Text>
            <Typography.Text strong>{t('worksheets.import.packet.make.name')}</Typography.Text>
            <Input
              value={packetName}
              maxLength={200}
              onChange={(e) => setPacketName(e.target.value)}
              data-testid="worksheet-import-make-name"
            />
            <Typography.Text strong>{t('worksheets.import.packet.make.description')}</Typography.Text>
            <Input.TextArea
              value={packetDescription}
              maxLength={2000}
              autoSize={{ minRows: 2 }}
              onChange={(e) => setPacketDescription(e.target.value)}
              data-testid="worksheet-import-make-description"
            />
            <Typography.Text strong>{t('worksheets.import.packet.make.steps')}</Typography.Text>
            <Typography.Text type="secondary">{sections[0].section}</Typography.Text>
            <List
              size="small"
              bordered
              dataSource={sections[0].steps}
              data-testid="worksheet-import-make-steps"
              renderItem={(step) => (
                <List.Item data-testid={`worksheet-import-make-step-${step.stepId}`}>
                  <Space>
                    <Tag>{t(`homework.kind.${step.kind}`)}</Tag>
                    <Typography.Text>{step.title}</Typography.Text>
                  </Space>
                </List.Item>
              )}
            />
            <Typography.Text type="secondary">{t('worksheets.import.packet.make.stepsHelp')}</Typography.Text>
            {packetProblem ? (
              <Alert type="error" showIcon message={packetProblem} data-testid="worksheet-import-make-error" />
            ) : null}
            <Button
              type="primary"
              loading={packetBusy}
              disabled={!packetName.trim() || sections[0].steps.length === 0}
              onClick={makePacket}
              data-testid="worksheet-import-make-submit"
            >
              {t('worksheets.import.packet.make.make')}
            </Button>
          </Space>
        </Card>
      ) : null}
    </Space>
  );
};
